import { AppBar, Box, Button, Container, Drawer, IconButton, InputBase, Toolbar, Typography } from "@mui/material";
import Menu from "@mui/icons-material/Menu";
import { PropsWithChildren, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/API";
import { MobileSearchResults } from "../pages/task/search/MobileSearchResults";
import { SearchField } from "../pages/task/search/SearchField";
import { useCurrentUser } from "./CurrentUser";
import { HelpDialog } from "./HelpDialog";
import { MobileTabBar } from "./MobileTabBar";
import { NavList, navWidth } from "./NavList";
import { SidePanelContext, useSidePanelSlot } from "./SidePanel";
import { useSnackAction } from "./SnackAction";
import { useUrlState } from "./urlParams";
import { usePhone } from "./usePhone";
import { UserMenu } from "./UserMenu";
import { WelcomeDialog } from "./WelcomeDialog";
import { useIsPlainAdmin, useWorkspace } from "./Workspace";
import { WorkspaceChip } from "./WorkspaceSwitcher";

export const sidePanelWidth = 600;

export function Layout(props: PropsWithChildren<{ fullBleed?: boolean }>) {
    const [navOpen, setNavOpen] = useState(false);
    const [help, setHelp] = useState(false);
    const panel = useSidePanelSlot();
    const phone = usePhone();
    const plainAdmin = useIsPlainAdmin();
    const ws = useWorkspace();

    // a phone search lives in the url, so the back button and an opened task keep it
    const url = useUrlState();
    const q = url.text("q");
    const [searching, setSearching] = useState(!!q);
    const showResults = phone && !plainAdmin && !!q;

    // "?" opens the help from anywhere a key isn't being typed into a field
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "?" || e.metaKey || e.ctrlKey || e.altKey) return;

            const target = e.target as HTMLElement;
            if (target.isContentEditable || target.closest("input, textarea, select")) return;

            e.preventDefault();
            setHelp(true);
        };

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const content = (
        <SidePanelContext.Provider value={panel.slot}>
            {props.children}
        </SidePanelContext.Provider>
    );

    return (
        <Box style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <MasqueradeBanner />

            <AppBar position="static" elevation={0}>
                <Toolbar variant="dense" sx={{ minHeight: 44, gap: 1 }}>
                    {phone && searching ? (
                        <>
                            <InputBase
                                autoFocus
                                fullWidth
                                placeholder="Search tasks"
                                value={q}
                                onChange={e => url.set({ q: e.target.value }, { replace: true })}
                                sx={{ color: "inherit", flex: 1 }}
                            />
                            <Button color="inherit" onClick={() => {
                                setSearching(false);
                                url.set({ q: "" }, { replace: true });
                            }}>
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <>
                            <IconButton color="inherit" edge="start" size="small" sx={{ display: { md: "none" } }}
                                        onClick={() => setNavOpen(true)}>
                                <Menu fontSize="small" />
                            </IconButton>

                            {/* a phone names the workspace where the logo would be */}
                            {phone && !plainAdmin ? <WorkspaceChip /> : (
                                <Typography variant="subtitle1" component={Link}
                                            to={plainAdmin ? "/admin" : ws.path("/tasks")}
                                            style={{ color: "inherit", textDecoration: "none", fontWeight: 500 }}>
                                    TSK
                                </Typography>
                            )}

                            {!phone && !plainAdmin && (
                                <Box sx={{ ml: 3 }}>
                                    <SearchField />
                                </Box>
                            )}

                            <Box style={{ flex: 1 }} />

                            <UserMenu onHelp={plainAdmin ? undefined : () => setHelp(true)} />
                        </>
                    )}
                </Toolbar>
            </AppBar>

            <Box style={{ display: "flex", flex: 1, minHeight: 0 }}>
                <Box sx={{
                    display: { xs: "none", md: "block" },
                    width: navWidth,
                    flexShrink: 0,
                    borderRight: 1,
                    borderColor: "divider",
                    overflow: "auto",
                }}>
                    <NavList onNavigate={() => {}} />
                </Box>

                {showResults && (
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
                        <MobileSearchResults q={q} />
                    </Box>
                )}

                {/* on a phone there's no room for both, so the panel takes over. The page
                    stays mounted under a search, so it's as it was when the search closes */}
                <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "auto",
                    display: showResults ? "none" : panel.open ? { xs: "none", md: "block" } : "block",
                }}>
                    {/* a full-bleed page runs to the edges and pads itself where it wants to.
                        It's a flex column the height of the viewport, so a page can pin a
                        section to the bottom and scroll another inside; a page taller than
                        that overflows it and scrolls here as before */}
                    {props.fullBleed ? (
                        <Box style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            {content}
                        </Box>
                    ) : (
                        <Container maxWidth="lg" style={{ paddingTop: 24, paddingBottom: 24 }}>
                            {content}
                        </Container>
                    )}
                </Box>

                <Box ref={panel.setHost} sx={{
                    display: panel.open && !showResults ? "flex" : "none",
                    flexShrink: 0,
                    minHeight: 0,
                    width: { xs: "100%", md: sidePanelWidth },
                    borderLeft: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }} />
            </Box>

            {phone && !plainAdmin && (
                <MobileTabBar
                    searching={searching}
                    onSearch={() => setSearching(true)}
                    onNavigate={() => setSearching(false)}
                />
            )}

            <Drawer
                variant="temporary"
                open={navOpen}
                onClose={() => setNavOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { md: "none" }, "& .MuiDrawer-paper": { width: navWidth } }}
            >
                <NavList onNavigate={() => setNavOpen(false)} />
            </Drawer>

            {!plainAdmin && <WelcomeDialog />}
            <HelpDialog open={help && !plainAdmin} onClose={() => setHelp(false)} />
        </Box>
    );
}

// MasqueradeBanner says whose eyes the admin is looking through, on every page
function MasqueradeBanner() {
    const me = useCurrentUser();

    const stop = useSnackAction(async () => {
        await api.admin.stopMasquerade({});

        // full page load so every provider re-initializes against the admin session
        window.location.href = "/admin";
    }, { success: "Back to admin" });

    if (!me || !me.masqueradeBy) return null;

    const name = [me.firstName, me.lastName].filter(v => !!v).join(" ") || me.email;

    return (
        <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
            px: 2, py: 0.5,
            bgcolor: "warning.main", color: "warning.contrastText",
        }}>
            <Typography variant="body2" noWrap>Viewing as {name}</Typography>
            <Button size="small" color="inherit" variant="outlined" disabled={stop.loading}
                    onClick={() => stop.callback(null)}>
                Stop
            </Button>
        </Box>
    );
}
